//#region Imports Packages and Libs

import { Component, Prop, Vue } from "vue-facing-decorator";
import { STORE_MUTATION } from "../../enums/store_mutation";
import { store } from "../../store/store";
import { FeedbackRating } from "../../types";
import { createLogger } from "./../../helpers/logger";
import {
  FeedbackKeys,
  IFeedbackCooldownEntry,
  MessageFeedbackPayload,
  SaveFeedbackParams,
} from "./../../types/chat/feedback";
import { FeedbackService } from "../../service/message/feedback_service";
import { __vueEvent } from "../../constant";
import { EVENTS } from "../../enums";
import { Util } from "../../util";
import { IMessage } from "../../interfaces";
import { getDefaultFeedbackFormLabel, resolveFeedbackFormLabel } from "../../helpers/i18n/label_resolver";

//#endregion

//#region Define the component in class-style

/**
 * Vue component for handling message feedback (thumbs up/down).
 * Manages rating state, cooldowns, and UI interactions.
 */
@Component({})
export default class MessageFeedback extends Vue {
  //#region Class properties will be component data

  /** @property messageId - ID of the message to rate. */
  @Prop() messageId?: string;
  /** @property extMsgId from message Store. */
  @Prop() extMsgId?: string;
  /** @property currentRating - Current message feedback rating. */
  @Prop() currentRating?: FeedbackRating;
  /** @property isInCooldown - Indicates if feedback is in cooldown. */
  @Prop({ default: false }) isInCooldown?: boolean;
  /** @property isRatingPending - Indicates if message rating is pending. */
  @Prop({ default: false }) isRatingPending?: boolean;

  // Logger instance for logging errors and messages
  private logger = createLogger({ context: "message_feedback.ts" });

  // For TESTING purposes only
  remainingTime: number = 0;
  countdownInterval: ReturnType<typeof setInterval> | null = null;

  // To display error messages in the UI (if needed)
  errorMessage: string = "";

  //#endregion

  //#region Computed Properties

  /**
   * Checks if thumbs up is selected.
   * @returns True if thumbs up.
   */
  get isThumbsUp(): boolean {
    try {
      const rating = (this.currentRating ?? 0) as FeedbackRating;
      return rating === FeedbackRating.ThumbsUp;
    } catch {
      return false;
    }
  }

  /**
   * Checks if thumbs down is selected.
   * @returns True if thumbs down.
   */
  get isThumbsDown(): boolean {
    try {
      const rating = (this.currentRating ?? 0) as FeedbackRating;
      return rating === FeedbackRating.ThumbsDown;
    } catch {
      return false;
    }
  }

  //#endregion

  //#region Lifecycle Methods

  /**
   * Called when the component is mounted.
   */
  mounted() {
    //this.startCountdown();
    this.refreshDomLabels(); // initialize labels on first render
  }

  /**
   * Cleanup timeouts when component is destroyed.
   */
  beforeDestroy(): void {
    try {
      this.clearComponentTimeouts();

      // Clear countdown interval
      // if (this.countdownInterval) {
      //   clearInterval(this.countdownInterval);
      // }
    } catch (ex) {
      this.logError("beforeDestroy", ex);
    }
  }

  //#endregion

  //#region Helper methods

  /**
   * Check whether the rating can be toggled right now.
   * - Ensures we have a messageId
   * - Prevents toggling while cooldown or pending request is active
   * @param choice - The rating choice user selected
   * @returns boolean - true when toggle is allowed
   */
  private canToggle(choice: FeedbackRating): boolean {
    // Quick guards: need a message and no concurrent activity
    if (!this.messageId) return false;
    if (this.isInCooldown || this.isRatingPending) return false;
    return true;
  }

  /**
   * Build both the UI payload (for optimistic updates) and
   * the API payload (for server submission).
   *
   * Normalizes values (e.g. extBotId -> number) and ensures a
   * safe string representation for the rating.
   *
   * @param newRating - rating to be applied
   * @returns object containing uiPayload and apiPayload
   */
  private buildPayloads(newRating: FeedbackRating): {
    uiPayload: MessageFeedbackPayload;
    apiPayload: SaveFeedbackParams;
  } {
    const uiPayload: MessageFeedbackPayload = {
      messageId: this.messageId || "",
      rating: newRating,
    };

    const apiPayload: SaveFeedbackParams = {
      // Ensure extBotId is a number (fallback to 0 if missing)
      ExtBotId: Number(store.state.extBotId) || 0,
      ExtMsgId: this.extMsgId || "",
      Key: FeedbackKeys.Rating,
      // Safe conversion: if newRating is nullish, fall back to Cleared
      Value: newRating != null ? newRating.toString() : FeedbackRating.Cleared.toString(),
      ConvId: store.state.convId || "",
      CompanyId: Number(store.state.companyId) || 0,
    };

    return { uiPayload, apiPayload };
  }

  /**
   * Apply optimistic UI update for the message rating.
   * Uses the existing Vuex mutation to keep a single source of truth.
   *
   * @param uiPayload - payload used by the UI/store mutation
   */
  private optimisticSetRating(uiPayload: MessageFeedbackPayload): void {
    // Commit optimistic change so UI updates immediately
    store.commit(STORE_MUTATION.SetMessageRating, uiPayload);
  }

  /**
   * Rollback the optimistic rating to the previous value.
   * Safe-guards if messageId is missing.
   *
   * @param prev - previous rating to restore
   */
  private rollbackRating(prev: FeedbackRating): void {
    try {
      if (!this.messageId) return;
      // Revert optimistic update using dedicated rollback mutation
      store.commit(STORE_MUTATION.RollbackMessageRating, {
        messageId: this.messageId,
        rating: prev,
      });
    } catch (ex) {
      this.logError("rollbackRating", ex);
    }
  }

  /**
   * Confirm the optimistic rating after successful server response.
   * No-op if messageId is missing.
   */
  private confirmRating(): void {
    if (!this.messageId) return;
    // Confirm the optimistic change so store marks it as persisted
    store.commit(STORE_MUTATION.ConfirmMessageRating, this.messageId);
  }

  /**
   * Send the rating payload to the feedback service.
   *
   * @param apiPayload - payload shaped for the backend API
   * @returns Promise resolving with the service response
   */
  private async sendRatingToService(apiPayload: SaveFeedbackParams) {
    const svc = new FeedbackService();
    return svc.saveMessageRating(apiPayload);
  }

  //#endregion
  
  //#region Refresh DOM Labels - SD-10045: Implementing multi language with Feedback form
  
  /**
   * Retrieve a localized label specific to the feedback form modal by key, using default labels as fallback.
   */
  private getFeedbackLabel(key: string): string {
    try {
      const pageConfig = this.$store.state.pageConfig;
      return resolveFeedbackFormLabel(key, pageConfig) as string;
    } catch (ex) {
      return getDefaultFeedbackFormLabel(key) as string;
    }
  }
  
  /**
   * Sets multiple attributes on a target element if it implements setAttribute. 
   * @param element - The target to receive attributes. 
   * @param attributes - A record mapping attribute names to string values. 
   */
  private setElementAttributes(element: unknown, attributes: Record<string, string>): void {
    try {
      if (!element || !(element as HTMLElement).setAttribute) return;
      for (const [attrName, attrValue] of Object.entries(attributes)) {
        (element as HTMLElement).setAttribute(attrName, attrValue);
      }
    } catch {}
  }

  /**
   * This method refreshes text content, placeholders, titles, and aria attributes
   * for various UI elements in the transcript modal based on configured labels.
   */
  private refreshDomLabels(): void {
    try {
      this.$nextTick(() => {
        // Close button (title + aria-label)
        const tooltipThumbsUp = this.getFeedbackLabel("tooltip_thumbs_up")
        const tooltipThumbsDown = this.getFeedbackLabel("tooltip_thumbs_down")
        
        // Set Tooltip attributes
        this.setElementAttributes(this.$refs.btnHelpful, { title: tooltipThumbsUp, "aria-label": tooltipThumbsUp });
        this.setElementAttributes(this.$refs.btnUnhelpful, { title: tooltipThumbsDown, "aria-label": tooltipThumbsDown });
      });
    } catch (error) {
      this.logError("refreshDomLabels", error);
    }
  }
  
  //#endregion

  //#region Main methods

  /**
   * Logs errors in a standardized format.
   * @param methodName - Method where error occurred.
   * @param error - The error.
   * @param context - Optional context.
   */
  logError(methodName: string, error: unknown, context?: string): void {
    const contextInfo = context ? ` - ${context}` : "";
    this.logger.error(`Error in ${methodName}${contextInfo}:`, error);
  }

  /**
   * Clears all timeouts associated with this component's messageId.
   */
  clearComponentTimeouts(): void {
    try {
      if (!this.messageId) return;

      const cooldown = store.state.feedbackCooldowns?.[this.messageId] as IFeedbackCooldownEntry;
      if (cooldown?.timeoutId) {
        clearTimeout(cooldown.timeoutId);
        store.commit(STORE_MUTATION.ClearMessageFeedbackCooldown, this.messageId);
      }
    } catch (ex) {
      this.logError("clearComponentTimeouts", ex);
    }
  }

  /**
   * Toggles feedback rating by choice.
   * @param choice - Rating to toggle.
   */
  async toggleByChoice(choice: FeedbackRating) {
    try {
      // Check if the rating can be toggled
      if (!this.canToggle(choice)) return;
      this.clearError();

      // Get previous and new rating
      const prev = (this.currentRating ?? 0) as FeedbackRating;
      const newRating = prev === choice ? FeedbackRating.Cleared : choice;

      const { uiPayload, apiPayload } = this.buildPayloads(newRating);

      // Optimistic Update: Initialize feedback if needed and set new rating
      this.optimisticSetRating(uiPayload);

      // Send feedback to server
      const response = await this.sendRatingToService(apiPayload);

      // Handle successful response, when "status" is true

			// TODO: REMOVE BEFORE PROD.
			// TESTING ONLY: force true so the block always runs for demo.
			// Currently the backend returns false for all ratings, so we need to simulate a successful response.
      if (true || !!response?.status) {
        this.confirmRating();
        // Emit event to open feedback modal (only for thumbs down)
        // If user clicked down button, open modal after rating success/optimistic change
        if (newRating === FeedbackRating.ThumbsDown && this.shouldShowFeedbackModal(this.messageId)) {
          this.openFeedbackModal(newRating);
        }
      } else {
        // Revert optimistic UI change on API call failure
        this.rollbackRating(prev);
        this.logError("toggleByChoice", response?.statusMessage, `Failed to save rating. Please try again.`);
      }
    } catch (ex) {
      // rollback on exception
      this.rollbackRating((this.currentRating ?? 0) as FeedbackRating);
      this.logError("toggleByChoice", ex, `Failed to save rating. Please try again.`);
    }
  }

  /**
   * Toggles thumbs up rating.
   */
  async toggleThumbsUp() {
    await this.toggleByChoice(FeedbackRating.ThumbsUp);
  }

  /**
   * Toggles thumbs down rating.
   */
  async toggleThumbsDown() {
    await this.toggleByChoice(FeedbackRating.ThumbsDown);
  }

  /**
   * Decide if the feedback (comment) modal should be shown for a messageId.
   *
   * Rules:
   * - Returns true only when:
   *   store.state.messages contains a message with id === messageId AND
   *   message.contents.settings.feedback.enableComment === true
   *
   * @param messageId - the IMessage.id to check
   * @returns boolean - true if modal should be shown, false otherwise
   */
  shouldShowFeedbackModal(messageId?: string | undefined): boolean {
    try {
      if (!messageId) return false;

      // Ensure store and messages are shaped as expected
      const rootState = (store && (store as any).state) || null;
      if (!Util.isPlainObject(rootState)) return false;

      const messages = (rootState as any).messages;
      if (!Array.isArray(messages)) return false;

      // Find the message by id (strict equality)
      const msg = (messages as IMessage[]).find((m) => m && m.id === messageId);
      if (!Util.isPlainObject(msg)) return false;

      // Validate contents -> settings -> feedback safely
      const contents = (msg as any).contents;
      if (!Util.isPlainObject(contents)) return false;

      const settings = (contents as any).settings;
      if (!Util.isPlainObject(settings)) return false;

      const feedback = (settings as any).feedback;
      if (!Util.isPlainObject(feedback)) return false;

      // Only show modal when enableComment is explicitly boolean true
      return (feedback as { enableComment?: unknown }).enableComment === true;
    } catch (ex) {
      this.logError("shouldShowFeedbackModal", ex);
    }
  }

  /**
   * This method starts the countdown for the feedback cooldown.
   */
  startCountdown() {
    try {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      this.countdownInterval = setInterval(() => {
        const cooldown = this.$store.state.feedbackCooldowns[this.messageId || ""] as IFeedbackCooldownEntry;
        if (cooldown) {
          this.remainingTime = Math.ceil((cooldown.expiry - Date.now()) / 1000);
          if (this.remainingTime <= 0) {
            this.remainingTime = 0;
            if (this.countdownInterval) {
              clearInterval(this.countdownInterval);
              this.countdownInterval = null;
            }
          }
        }
      }, 100);
    } catch (ex) {
      this.logError("startCountdown", ex);
    }
  }

  /**
   * Opens the feedback modal, allowing the user to provide feedback on the message.
   */
  openFeedbackModal(currentRating: FeedbackRating) {
    try {
      __vueEvent.emit(EVENTS.ShowFeedbackModal, {
        messageId: this.messageId,
        extMsgId: this.extMsgId,
        currentRating,
      });
    } catch (ex) {
      this.logError("openFeedbackModal", ex);
    }
  }

  /**
   * Closes the feedback modal.
   */
  closeFeedbackModal() {
    __vueEvent.emit(EVENTS.HideFeedbackModal);
  }

  /**
   * Displays an error message in the UI.
   */
  showError(message: string) {
    this.errorMessage = message;
    // Auto-clear error after 3 seconds
    setTimeout(() => this.clearError(), 3000);
  }

  /**
   * Clears the error message from the UI, after a timeout.
   */
  clearError() {
    this.errorMessage = "";
  }

  //#endregion
}

//#endregion
